import React, { useMemo, useState } from 'react';
import { roiMultiple } from '../utils/roiMultiple';
import { ChevronDownIcon } from 'lucide-react';
import { CalculationResult, Inputs, usesRevenueTrail } from '../utils/roiCalculations';
import { calculatePestRepEconomics } from '../utils/pestRepEconomics';
import { PestRepEconomics } from './PestRepEconomics';
import { RepEconomics as RepEconomicsData, calculateRepEconomics } from '../utils/repEconomics';
import { fmtMoney } from '../utils/formatters';
interface RepEconomicsProps {
  inputs: Inputs;
  results: CalculationResult;
  halfSales?: boolean;
  doubleAttrition?: boolean;
}
export function RepEconomics({
  inputs,
  results,
  halfSales = false,
  doubleAttrition = false
}: RepEconomicsProps) {
  // Hidden until someone chooses to show it.
  const [isOpen, setIsOpen] = useState(false);
  const economics = useMemo(() => calculateRepEconomics(inputs, results, {
    halfSales,
    doubleAttrition
  }), [inputs, results, halfSales, doubleAttrition]);
  const scenarioActive = halfSales || doubleAttrition;
  // Pest and Summer Pest run their own year-one + revenue-trail math.
  const pest = usesRevenueTrail(inputs.industry);
  const pestEconomics = useMemo(() => pest ? calculatePestRepEconomics(inputs, results, {
    halfSales,
    doubleAttrition
  }) : null, [pest, inputs, results, halfSales, doubleAttrition]);
  return <section aria-labelledby="rep-onomics-heading" data-id="element-1320" data-mp="dpawoawap21j">
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 pb-2" data-id="element-1321" data-mp="8sc3bukggz4e">
        <h3 id="rep-onomics-heading" className="text-lg font-bold text-navy flex items-center gap-2" data-id="element-1322" data-mp="ngf9p52euctq">
          Rep-onomics
          {scenarioActive && <span className="text-xs font-bold text-orange-600" data-id="element-1323" data-mp="whd5x16lxg1r">
              (Scenario Active)
            </span>}
        </h3>
        <button type="button" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} aria-controls="rep-onomics-panel" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-gray-500 hover:text-amber-500 transition-colors" data-id="element-1324" data-mp="sgt6fqyka0cg">
          {isOpen ? 'Hide' : 'Show'}
          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1325" data-mp="1e7m70av2vrpv" />
        </button>
      </div>

      {isOpen && <div id="rep-onomics-panel" className="pt-4" data-id="element-1326" data-mp="1n812u45o4p7m">
          <p className="text-sm text-gray-600 mb-5" data-id="element-1327" data-mp="1nanb660dv8qw">
            {pest ? 'What it costs to recruit one rep who clears day 90, and what that rep’s accounts are worth as they keep paying.' : 'What it costs to put one rep past Day 90, against the gross profit that rep brings in.'}
          </p>
          {pest ? pestEconomics ? <PestRepEconomics economics={pestEconomics} inputs={inputs} halfSales={halfSales} doubleAttrition={doubleAttrition} data-id="element-1328" data-mp="2t8yqect5kuy" /> : <p className="text-sm text-gray-600" data-id="element-1329" data-mp="2bafld5o3web3">
                No reps clear day 90 with the current assumptions, so
                there&rsquo;s nothing to compare yet.
              </p> : economics ? <RepEconomicsBody economics={economics} hasResiduals={inputs.revenueCadence !== 'one-time' && !!inputs.residualTicket} halfSales={halfSales} doubleAttrition={doubleAttrition} data-id="element-1330" data-mp="2z2cv8r7zww9k" /> : <p className="text-sm text-gray-600" data-id="element-1331" data-mp="2l2v2y1vhr0vg">
              No reps reach Day 90 with the current assumptions, so there&rsquo;s
              nothing to compare yet.
            </p>}
        </div>}
    </section>;
}
interface RepEconomicsBodyProps {
  economics: RepEconomicsData;
  hasResiduals: boolean;
  halfSales: boolean;
  doubleAttrition: boolean;
}
function RepEconomicsBody({
  economics,
  hasResiduals,
  halfSales,
  doubleAttrition
}: RepEconomicsBodyProps) {
  const {
    costPer90DayRep,
    hiresPer90DayRep,
    horizons,
    cohortCost,
    cohortFees,
    cohortAdSpend,
    cohortBonuses,
    cohortNinetyDayReps
  } = economics;
  // Bars share one scale so the cost marker lines up across every horizon.
  const scaleMax = Math.max(costPer90DayRep, ...horizons.map(h => h.grossProfit));
  const costPosition = scaleMax > 0 ? costPer90DayRep / scaleMax * 100 : 0;
  const notes: string[] = [];
  if (halfSales) notes.push('50% Sales halves what each rep sells');
  if (doubleAttrition) notes.push('2x Attrition halves the reps who reach each stage, so each 90-day rep costs twice as much (bonuses fall with them)');
  return <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 lg:gap-10" data-id="element-1332" data-mp="20tlt24lq3004">
      {/* The cost every horizon is measured against */}
      <div className="lg:border-r lg:border-gray-200 lg:pr-10" data-id="element-1333" data-mp="1t7zsr8phdglv">
        <div className="text-sm font-semibold text-gray-600" data-id="element-1334" data-mp="1obj3ucagiy7q">
          Cost per 90-day rep
        </div>
        <div className="mt-1 text-3xl md:text-4xl font-extrabold text-navy" data-id="element-1335" data-mp="229g5hn3rput9">
          {fmtMoney(costPer90DayRep)}
        </div>
        <p className="mt-3 text-sm text-gray-600" data-id="element-1336" data-mp="1xc7i983rj0l5">
          One steady campaign month: {fmtMoney(cohortCost)} (
          {fmtMoney(cohortFees)} fees + {fmtMoney(cohortAdSpend)} ads +{' '}
          {fmtMoney(cohortBonuses)} est. bonuses) ÷{' '}
          {cohortNinetyDayReps.toFixed(2)} reps from that month who reach Day
          90. About {hiresPer90DayRep.toFixed(1)} hires for every rep who makes
          it. The leader search isn&rsquo;t included. Returns are gross profit
          ÷ that cost, over the rep&rsquo;s first months from launch.
        </p>
      </div>

      {/* Gross profit from that rep over time */}
      <div data-id="element-1337" data-mp="2lyie4dgnxqw0">
        <div className="grid grid-cols-[96px_1fr_auto] md:grid-cols-[120px_1fr_200px] items-center gap-x-4 pb-2 border-b border-gray-200 text-xs font-semibold text-gray-500" data-id="element-1338" data-mp="2lysacisawua5">
          <span data-id="element-1339" data-mp="2qv8wj7fxwykl">Horizon</span>
          <span data-id="element-1340" data-mp="2vnsa44emntpe">Gross profit from one rep</span>
          <span className="text-right" data-id="element-1341" data-mp="2vp3loqebe5r2">Return</span>
        </div>
        <ul className="divide-y divide-gray-100" data-id="element-1342" data-mp="30md8rze8e5bq">
          {horizons.map(horizon => {
          const barWidth = scaleMax > 0 ? horizon.grossProfit / scaleMax * 100 : 0;
          const paysBack = horizon.net >= 0;
          return <li key={horizon.key} className="grid grid-cols-[96px_1fr_auto] md:grid-cols-[120px_1fr_200px] items-center gap-x-4 py-4" data-id="element-1343" data-mp="2cxu2fpeqs2ma">
                <span className="text-sm font-semibold text-navy whitespace-nowrap" data-id="element-1344" data-mp="2gx2tunmb7oeo">
                  {horizon.label}
                </span>

                <div className="min-w-0" data-id="element-1345" data-mp="3sdt3yhtut0dk">
                  <div className="flex items-baseline gap-2" data-id="element-1346" data-mp="3scrkboyrv5k8">
                    <span className="text-lg font-bold text-gray-900" data-id="element-1347" data-mp="1gq62zo7txf77">
                      {fmtMoney(horizon.grossProfit)}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-1348" data-mp="1cs01ovprfrto">
                      {horizon.sales.toFixed(1)} sales
                    </span>
                  </div>
                  <div className="relative mt-2 h-2 rounded-full bg-gray-100" aria-hidden="true" data-id="element-1349" data-mp="12xq8jbszuphw">
                    <div className={`absolute inset-y-0 left-0 rounded-full ${paysBack ? 'bg-green-600' : 'bg-amber'}`} style={{
                  width: `${barWidth}%`
                }} data-id="element-1350" data-mp="t1dcw8dw21ki" />
                    <div className="absolute -top-1 -bottom-1 w-0.5 bg-navy" style={{
                  left: `calc(${costPosition}% - 1px)`
                }} data-id="element-1351" data-mp="k2jbm4pm4n0m" />
                  </div>
                </div>

                <div className="text-right" data-id="element-1352" data-mp="2ed5183e66v77">
                  <div className={`text-lg font-bold ${paysBack ? 'text-green-600' : 'text-red-600'}`} data-id="element-1353" data-mp="1naoq5f5ky0ud">
                    {roiMultiple(horizon.multiple * 100)}
                  </div>
                  <div className="text-xs text-gray-600 whitespace-nowrap" data-id="element-1354" data-mp="1djk989wcg5ww">
                    {paysBack ? 'Net ' : 'Short '}
                    {fmtMoney(Math.abs(horizon.net))}
                  </div>
                </div>
              </li>;
        })}
        </ul>
        <p className="mt-3 text-xs text-gray-500" data-id="element-1355" data-mp="18lis3uihago2">
          <span className="inline-block align-middle w-0.5 h-3 bg-navy mr-1.5" data-id="element-1356" data-mp="uky9ozgh6mv3" />
          Marks the cost per 90-day rep. Cost is based on one steady month of
          the standard campaign; gross profit uses realized sales after pullthrough
          {hasResiduals ? ', including residual revenue' : ''}.
          {notes.length > 0 && ` ${notes.join('; ')}.`}
        </p>
      </div>
    </div>;
}